import React, { useState, useEffect, useRef } from "react";
import { fetchUserProfile, updateUserProfile, changeUserPassword, sendEmailVerification, updateLanguagePreference } from "../services/userService";
import { fetchUserCommunities } from "../services/communityService";
import { uploadProfileImage, fixImageUrl } from "../services/imageService";
import { useAuth } from "../contexts/AuthContext";
import { useNavigation } from "../contexts/NavigationContext";
import { useLanguage } from "../contexts/LanguageContext";
import MobileProfilePage from "./MobileProfilePage"; // Import the mobile component

// Function to validate email
const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
};

function ProfilePage() {
    const { csrfToken } = useAuth();
    const { isMobile } = useNavigation();
    const { t, setLanguage } = useLanguage();
    const [user, setUser] = useState({
        email: "",
        displayName: "",
        profileImage: "",
        role: "",
        group: "",
        community_id: null,
        email_verified: false,
        preferred_language: "en"
    });

    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [passwordData, setPasswordData] = useState({ old_password: "", new_password: "" });
    const [userCommunities, setUserCommunities] = useState([]);
    const [emailVerificationSent, setEmailVerificationSent] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);
    
    // Fetch user data and communities
    useEffect(() => {
        const loadUserData = async () => {
            try {
                // Fetch user profile
                const userData = await fetchUserProfile();
                if (userData) {
                    setUser(userData);
                    
                    // Fetch user communities
                    try {
                        const communitiesData = await fetchUserCommunities();
                        if (communitiesData && Array.isArray(communitiesData)) {
                            setUserCommunities(communitiesData);
                        }
                    } catch (err) {
                    }
                }
            } catch (error) {
                // Error will be handled by error state
            } finally {
                setLoading(false);
            }
        };

        loadUserData();
    }, [csrfToken]); // Keep csrfToken in dependency array for re-fetching if token changes

    // Handle profile update
    const handleChange = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value });
    };

    // Handle password input change
    const handlePasswordChange = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    // Handle profile update submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");
        setError("");

        // Validate email
        if (user.email && !validateEmail(user.email)) {
            setError(t('profile.errors.invalidEmail'));
            return;
        }

        try {
            const response = await updateUserProfile(user, csrfToken);
            if (response) {
                setMessage(t('profile.success.profileUpdated'));
            } else {
                setError(t('profile.errors.updateFailed'));
            }
        } catch (error) {
            setError("❌ " + (error.response?.data?.error || error.message || "An error occurred."));
        }
    };

    // Handle password change submission
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setMessage("");
        setError("");

        // Password validation
        if (passwordData.new_password.length < 8) {
            setError(t('profile.errors.passwordTooShort'));
            return;
        }

        try {
            const response = await changeUserPassword(passwordData, csrfToken);
            if (response) {
                setMessage(t('profile.success.passwordChanged'));
                setPasswordData({ old_password: "", new_password: "" });
            } else {
                setError(t('profile.errors.passwordChangeFailed'));
            }
        } catch (error) {
            setError("❌ " + (error.response?.data?.error || error.message || "An error occurred."));
        }
    };
    
    // Handle sending verification email
    const handleSendVerification = async () => {
        setMessage("");
        setError("");
        
        try {
            const response = await sendEmailVerification(csrfToken);
            if (response) {
                setMessage(t('profile.success.verificationSent'));
                setEmailVerificationSent(true);
            } else {
                setError(t('profile.errors.verificationFailed'));
            }
        } catch (error) {
            setError("❌ " + (error.response?.data?.error || error.message || "An error occurred."));
        }
    };

    // Trigger click on file input
    const handleUploadClick = () => {
        fileInputRef.current.click();
    };

    // Handle profile image file selection
    const handleFileChange = async (e) => {
        if (!e.target.files || e.target.files.length === 0) {
            return;
        }

        const file = e.target.files[0];

        // Simple image type check
        if (!file.type.startsWith('image/')) {
            setError(t('profile.errors.selectImageFile'));
            return;
        }

        setMessage("");
        setError("");
        setIsUploading(true);

        try {
            // Use uploadProfileImage service function
            const data = await uploadProfileImage(file, csrfToken);

            if (data && data.image_url) {
                setUser({ ...user, profileImage: data.image_url });
                setMessage(t('profile.success.imageUploaded'));
            } else {
                throw new Error("Invalid response from server");
            }
        } catch (error) {
            setError(t('profile.errors.uploadFailed'));
        } finally {
            setIsUploading(false);
        }
    };

    // Handle language preference change
    const handleLanguageChange = async (e) => {
        const newLanguage = e.target.value;
        setMessage("");
        setError("");

        try {
            const response = await updateLanguagePreference(newLanguage, csrfToken);
            if (response) {
                setUser({ ...user, preferred_language: newLanguage });
                setLanguage(newLanguage); // Update the language context
                // Note: Message will appear in the new language after context updates
                setMessage("✅ Language preference updated successfully!");
            }
        } catch (error) {
            setError("❌ " + (error.response?.data?.error || error.message || "Failed to update language preference."));
        }
    };

    // If mobile, render the dedicated mobile page instead
    if (isMobile) {
        return <MobileProfilePage />;
    }

    if (loading) return (
        <div className="container mt-5">
            <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                </div>
                <p className="mt-3">{t('profile.loading')}</p>
            </div>
        </div>
    );

    // Find primary community
    const primaryCommunity = userCommunities.find(c => c.is_primary);

    // Desktop view
    return (
        <div className="container mt-5">
            <h2>{t('profile.title')}</h2>
            {message && <div className="alert alert-success">{message}</div>}
            {error && <div className="alert alert-danger">{error}</div>}

            <div className="row">
                <div className="col-md-4">
                    <div className="card mb-4">
                        <div className="card-header">
                            <h4>{t('profile.profilePhoto')}</h4>
                        </div>
                        <div className="card-body text-center">
                            {user.profileImage ? (
                                <img 
                                    src={fixImageUrl(user.profileImage)} 
                                    alt="Profile" 
                                    className="img-fluid rounded-circle mb-3" 
                                    style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                                    onError={(e) => {
                                        e.target.onerror = null; // Prevent infinite error loop
                                        e.target.style.display = 'none'; // Hide broken image
                                        // Switch to initials
                                        setUser({...user, profileImage: ''});
                                    }}
                                />
                            ) : (
                                <div className="bg-light rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" 
                                     style={{ width: '150px', height: '150px' }}>
                                    <span className="display-4 text-secondary">
                                        {user.displayName ? user.displayName.charAt(0).toUpperCase() : '?'}
                                    </span>
                                </div>
                            )}
                            
                            <div className="mt-3">
                                <input 
                                    type="file" 
                                    ref={fileInputRef} 
                                    onChange={handleFileChange} 
                                    className="d-none" 
                                    accept="image/*"
                                />
                                <button 
                                    className="btn btn-outline-primary" 
                                    onClick={handleUploadClick}
                                    disabled={isUploading}
                                >
                                    {isUploading ? t('profile.uploading') : t('profile.uploadNewPhoto')}
                                </button>

                                {user.profileImage && (
                                    <button
                                        className="btn btn-outline-secondary ms-2"
                                        onClick={() => setUser({ ...user, profileImage: "" })}
                                    >
                                        {t('profile.remove')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="card mb-4">
                        <div className="card-header">
                            <h4>{t('profile.communityInformation')}</h4>
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <label className="form-label">{t('profile.primaryCommunity')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={primaryCommunity ? primaryCommunity.name : t('profile.none')}
                                    disabled
                                />
                            </div>

                            {userCommunities.length > 0 && (
                                <div className="mb-3">
                                    <label className="form-label">{t('profile.myCommunities')}</label>
                                    <ul className="list-group">
                                        {userCommunities.map(community => (
                                            <li key={community.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                {community.name}
                                                <div>
                                                    {community.is_primary && (
                                                        <span className="badge bg-primary rounded-pill me-1">{t('profile.primary')}</span>
                                                    )}
                                                    <span className="badge bg-secondary rounded-pill">{community.role}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="form-text mt-2">
                                        <a href="/communities" className="text-decoration-none">{t('profile.manageCommunities')}</a>
                                    </div>
                                </div>
                            )}

                            <div className="mb-3">
                                <label className="form-label">{t('profile.globalRole')}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={user.role || t('profile.user')}
                                    disabled
                                />
                            </div>
                            {user.group && (
                                <div className="mb-3">
                                    <label className="form-label">{t('profile.group')}</label>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        value={user.group} 
                                        disabled 
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-md-8">
                    <div className="card mb-4">
                        <div className="card-header">
                            <h4>{t('profile.accountInformation')}</h4>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label className="form-label">{t('profile.email')}</label>
                                    <div className="input-group">
                                        <input
                                            type="email"
                                            className="form-control"
                                            name="email"
                                            value={user.email || ""}
                                            disabled
                                        />
                                        {!user.email_verified && (
                                            <button
                                                type="button"
                                                className="btn btn-warning"
                                                onClick={handleSendVerification}
                                                disabled={emailVerificationSent}
                                            >
                                                {emailVerificationSent ? t('profile.emailSent') : t('profile.verifyEmail')}
                                            </button>
                                        )}
                                    </div>
                                    {!user.email_verified && (
                                        <div className="form-text text-warning">
                                            {t('profile.emailNotVerified')}
                                        </div>
                                    )}
                                    {user.email_verified && (
                                        <div className="form-text text-success">
                                            {t('profile.emailVerified')}
                                        </div>
                                    )}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('profile.displayName')}</label>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        name="displayName" 
                                        value={user.displayName || ""} 
                                        onChange={handleChange} 
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('profile.profileImageUrl')}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="profileImage"
                                        value={user.profileImage || ""}
                                        onChange={handleChange}
                                    />
                                    <div className="form-text">
                                        {t('profile.profileImageInstruction')}
                                    </div>
                                </div>
                                <button type="submit" className="btn btn-primary">{t('profile.updateProfile')}</button>
                            </form>
                        </div>
                    </div>

                    <div className="card mb-4">
                        <div className="card-header">
                            <h4>{t('profile.languagePreference')}</h4>
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <label className="form-label">{t('profile.preferredLanguage')}</label>
                                <select
                                    className="form-select"
                                    value={user.preferred_language || "en"}
                                    onChange={handleLanguageChange}
                                >
                                    <option value="en">English</option>
                                    <option value="sl">Slovenščina (Slovenian)</option>
                                    <option value="de">Deutsch (German)</option>
                                </select>
                                <div className="form-text">
                                    {t('profile.languageInstruction')}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card mb-4">
                        <div className="card-header">
                            <h4>{t('profile.changePassword')}</h4>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handlePasswordSubmit}>
                                <div className="mb-3">
                                    <label className="form-label">{t('profile.currentPassword')}</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        name="old_password"
                                        value={passwordData.old_password}
                                        onChange={handlePasswordChange}
                                        required
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('profile.newPassword')}</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        name="new_password"
                                        value={passwordData.new_password}
                                        onChange={handlePasswordChange}
                                        required
                                    />
                                    <div className="form-text">{t('profile.passwordMinLength')}</div>
                                </div>
                                <button type="submit" className="btn btn-danger">{t('profile.changePasswordButton')}</button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProfilePage;